import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FolderOpen,
  Plus,
  Search,
  X,
  ExternalLink,
  Ban,
  Plane,
  Hotel,
  Package,
  FileText,
  Bus,
  Shield,
  Car,
  CreditCard,
  Ship,
  ChevronDown,
  Check,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  Info,
  Calendar,
  Trash2,
  SlidersHorizontal,
} from "lucide-react";
import styles from "./BookingList.module.css";
import { useGetBookings, useGetBookingStatusCounts, useDeleteBooking } from "../../../api/booking/useBookingApi";
import { useAppSelector } from "../../../store/hooks";
import { selectUser } from "../../../store/selectors";
import { useToast } from "../../../context/ToastContext";
import DeleteConfirmModal from "../../../components/BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import BookingFilterDrawer from "./BookingFilterDrawer";
import {
  DEFAULT_FILTERS,
  activeFilterCount,
  toQueryParams,
  ORIGIN_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_LABEL,
} from "./bookingFilters";

const ALL_PRODUCT_TYPES = [
  { key: "Flight",    label: "Flight",     Icon: Plane },
  { key: "Hotel",     label: "Hotel",      Icon: Hotel },
  { key: "Transfer",  label: "Transfer",   Icon: Bus },
  { key: "Insurance", label: "Insurance",  Icon: Shield },
  { key: "Package",   label: "Package",    Icon: Package },
  { key: "CarRental", label: "Car Rental", Icon: Car },
  { key: "Visa",      label: "Visa",       Icon: FileText },
  { key: "Cruise",    label: "Cruise",     Icon: Ship },
];

const PRODUCT_ICON = {
  Flight: Plane, Hotel: Hotel, Transfer: Bus, Insurance: Shield,
  Package: Package, CarRental: Car, Visa: FileText, Cruise: Ship,
};

const PRODUCT_STYLE = {
  Flight:    { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
  Hotel:     { bg: "#fef3c7", color: "#b45309", border: "#fde68a" },
  Transfer:  { bg: "#ecfdf5", color: "#059669", border: "#a7f3d0" },
  Insurance: { bg: "#f3e8ff", color: "#7c3aed", border: "#ddd6fe" },
  Package:   { bg: "#fff1f2", color: "#e11d48", border: "#fecdd3" },
  CarRental: { bg: "#f0f9ff", color: "#0284c7", border: "#bae6fd" },
  Visa:      { bg: "#fef9ec", color: "#a16207", border: "#fde68a" },
  Cruise:    { bg: "#f0fdfa", color: "#0d9488", border: "#99f6e4" },
};

const mockBookings = [
  {
    id: 1,
    ref: "SNK-2025-00137",
    createdAt: "2025-05-20T14:32:00",
    invoiced: true,
    customer: { name: "TEKIN Ridvan", email: "tekin.r@email.com", type: "Private" },
    products: ["Flight"],
    startDate: "2025-06-15",
    startDateType: "Flight",
    productSummary: "BRU → AYT (OW)",
    origin: "Online",
    totalPrice: 1250.0,
    paidAmount: 1250.0,
    saldo: 0.0,
    lastPaymentDate: "2025-05-25",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
  },
  {
    id: 2,
    ref: "SNK-2025-00136",
    createdAt: "2025-05-20T14:20:00",
    invoiced: true,
    customer: { name: "YILMAZ Ahmet", email: "yilmaz.a@email.com", type: "Private" },
    products: ["Flight"],
    startDate: "2025-06-15",
    startDateType: "Flight",
    productSummary: "BRU ⇌ AYT (RT)",
    origin: "Online",
    totalPrice: 980.0,
    paidAmount: 980.0,
    saldo: 0.0,
    lastPaymentDate: "2025-05-25",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
  },
  {
    id: 3,
    ref: "SNK-2025-00135",
    createdAt: "2025-05-20T13:58:00",
    invoiced: true,
    customer: { name: "DEMIR Selin", email: "demir.s@email.com", type: "Private" },
    products: ["Hotel", "Hotel", "Transfer"],
    startDate: "2025-07-22",
    startDateType: "Hotel",
    productSummary: "Antalya – 7N\nAYT Airport → Lara",
    origin: "Offline",
    totalPrice: 2340.0,
    paidAmount: 1500.0,
    saldo: 840.0,
    lastPaymentDate: "2025-07-01",
    bookingStatus: "Confirmed",
    paymentStatus: "Partial",
  },
  {
    id: 4,
    ref: "SNK-2025-00134",
    createdAt: "2025-05-20T13:42:00",
    invoiced: false,
    customer: { name: "KAYA Murat", email: "kaya.m@email.com", type: "Private" },
    products: ["CarRental"],
    startDate: "2025-07-22",
    startDateType: "Car Rental",
    productSummary: "Antalya Airport – Renault Clio",
    origin: "Offline",
    totalPrice: 320.0,
    paidAmount: 320.0,
    saldo: 0.0,
    lastPaymentDate: "2025-07-01",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
  },
  {
    id: 5,
    ref: "SNK-2025-00133",
    createdAt: "2025-05-20T11:25:00",
    invoiced: true,
    customer: { name: "ARSLAN Mehmet", email: "arslan.m@email.com", type: "Private" },
    products: ["Flight", "Hotel", "Transfer", "CarRental"],
    startDate: "2025-07-22",
    startDateType: "Flight",
    productSummary: "BRU → AYT → DXB → BRU (RT-Multi)\nAntalya – 7N\nAYT Airport → Lara",
    origin: "Online",
    totalPrice: 2750.0,
    paidAmount: 1000.0,
    saldo: 1750.0,
    lastPaymentDate: "2025-07-01",
    bookingStatus: "Pending",
    paymentStatus: "Partial",
  },
  {
    id: 6,
    ref: "SNK-2025-00132",
    createdAt: "2025-05-19T16:40:00",
    invoiced: false,
    customer: { name: "CETIN Elif", email: "cetin.e@email.com", type: "Private" },
    products: ["Insurance"],
    startDate: "2025-07-23",
    startDateType: "Insurance",
    productSummary: "Allianz Premium",
    origin: "Online",
    totalPrice: 85.0,
    paidAmount: 85.0,
    saldo: 0.0,
    lastPaymentDate: "2025-07-02",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
  },
  {
    id: 7,
    ref: "SNK-2025-00131",
    createdAt: "2025-05-19T10:47:00",
    invoiced: true,
    customer: { name: "BAYRAK Ozan", email: "bayrak.o@email.com", type: "Private" },
    products: ["Cruise"],
    startDate: "2025-08-05",
    startDateType: "Cruise",
    productSummary: "MSC Seaside – Mediterranean",
    origin: "Offline",
    totalPrice: 1680.0,
    paidAmount: 1900.0,
    saldo: -220.0,
    lastPaymentDate: "2025-07-15",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
  },
  {
    id: 8,
    ref: "SNK-2025-00130",
    createdAt: "2025-05-18T09:15:00",
    invoiced: true,
    customer: { name: "ÖZTÜRK Deniz", email: "ozturk.d@email.com", type: "Private" },
    products: ["Package"],
    startDate: "2025-08-10",
    startDateType: "Package",
    productSummary: "Antalya Package – 7N",
    origin: "Online",
    totalPrice: 1490.0,
    paidAmount: 1490.0,
    saldo: 0.0,
    lastPaymentDate: "2025-07-20",
    bookingStatus: "Confirmed",
    paymentStatus: "Paid",
  },
];

const STATUS_TABS = [
  { key: "All", label: "All Bookings" },
  { key: "Draft", label: "Draft" },
  { key: "Confirmed", label: "Confirmed" },
  { key: "Pending", label: "Pending" },
  { key: "Cancelled", label: "Cancelled" },
];

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return "—"; }
};

const formatDateTime = (d) => {
  if (!d) return { date: "—", time: "" };
  try {
    const dt = new Date(d);
    return {
      date: dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }),
      time: dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    };
  } catch { return { date: "—", time: "" }; }
};

const getDaysBefore = (payDate, serviceDate) => {
  if (!payDate || !serviceDate) return null;
  try {
    const diff = Math.round((new Date(serviceDate) - new Date(payDate)) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  } catch { return null; }
};

// ── Product summary builder per type ─────────────────────────────────────
const buildFlightSummary = (p) => {
  const legs = p.productDetails?.legs || [];
  if (legs.length === 0) return null;
  const type = p.productDetails?.flightType || "";
  const airports = legs.map((l) => l.departureAirport).filter(Boolean);
  const lastArr = legs[legs.length - 1]?.arrivalAirport;
  if (lastArr) airports.push(lastArr);
  if (airports.length < 2) return null;
  if (type === "RT" && airports.length === 2) {
    return `${airports[0]} ⇌ ${airports[1]} (RT)`;
  }
  if (airports.length > 2) {
    const label = type === "RT" ? "RT-Multi" : "OW-M";
    return `${airports.join(" → ")} (${label})`;
  }
  return `${airports[0]} → ${airports[1]} (OW)`;
};

const buildHotelSummary = (p) => {
  const d = p.productDetails || {};
  const name = d.hotelName || d.destination || "";
  const nights = d.nights || d.days || "";
  if (!name && !nights) return null;
  return nights ? `${name || "Hotel"} – ${nights}N` : name;
};

const buildTransferSummary = (p) => {
  const d = p.productDetails || {};
  const s = d.schedule || {};
  const from = (s.pickupLocation || "").trim();
  const to = (s.dropoffLocation || "").trim();
  if (from || to) {
    const shorten = (loc) => loc.length > 25 ? loc.substring(0, 22) + "…" : loc;
    return `${shorten(from || "—")} → ${shorten(to || "—")}`;
  }
  // Fallback: show moment + vehicle type
  const parts = [d.transferMoment, d.transferType, d.vehicleType].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
};

const buildInsuranceSummary = (p) => {
  const d = p.productDetails || {};
  return d.providerName || d.planName || d.insuranceName || "Insurance";
};

const buildCarRentalSummary = (p) => {
  const d = p.productDetails || {};
  const loc = d.pickupLocation || d.location || "";
  const vehicle = d.vehicleName || d.vehicleType || "";
  if (!loc && !vehicle) return null;
  return [loc, vehicle].filter(Boolean).join(" – ");
};

const buildVisaSummary = (p) => {
  const d = p.productDetails || {};
  const country = d.destinationCountry || d.country || "";
  const type = d.visaType || "Tourist Visa";
  return country ? `${country} ${type}` : type;
};

const buildCruiseSummary = (p) => {
  const d = p.productDetails || {};
  const ship = d.shipName || d.cruiseLine || "";
  const itin = d.itinerary || d.destination || "";
  return [ship, itin].filter(Boolean).join(" – ") || "Cruise";
};

const buildPackageSummary = (p) => {
  const d = p.productDetails || {};
  const dest = d.destination || d.packageName || "";
  const nights = d.nights || d.days || "";
  return nights ? `${dest || "Package"} – ${nights}N` : dest || "Package";
};

const SUMMARY_BUILDERS = {
  Flight: buildFlightSummary,
  Hotel: buildHotelSummary,
  Transfer: buildTransferSummary,
  Insurance: buildInsuranceSummary,
  CarRental: buildCarRentalSummary,
  Visa: buildVisaSummary,
  Cruise: buildCruiseSummary,
  Package: buildPackageSummary,
};

/** Product type priority for ordering */
const PRODUCT_ORDER = ["Flight", "Hotel", "Transfer", "Insurance", "Package", "CarRental", "Visa", "Cruise"];

/** Build structured product summaries: ALL products, ordered by type priority */
const buildProductSummaries = (rawProducts) => {
  if (!rawProducts || rawProducts.length === 0) return [];
  const sorted = [...rawProducts]
    .filter((p) => p.productType)
    .sort((a, b) => {
      const ai = PRODUCT_ORDER.indexOf(a.productType);
      const bi = PRODUCT_ORDER.indexOf(b.productType);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  return sorted.map((p) => {
    const builder = SUMMARY_BUILDERS[p.productType];
    const text = builder ? builder(p) : p.productType;
    return { type: p.productType, text: text || p.productType };
  });
};

/** Get service date from a raw product object */
const getProductServiceDate = (p) =>
  p.serviceDate || p.productDetails?.checkIn || p.productDetails?.legs?.[0]?.departureDate || null;

const mapApiBooking = (b) => {
  const products = (b.products || []).map((p) => p.productType).filter(Boolean);
  const summaries = buildProductSummaries(b.products || []);

  // Derive start date from the primary (highest-priority) product
  const sortedProducts = [...(b.products || [])]
    .filter((p) => p.productType)
    .sort((a, c) => {
      const ai = PRODUCT_ORDER.indexOf(a.productType);
      const ci = PRODUCT_ORDER.indexOf(c.productType);
      return (ai === -1 ? 99 : ai) - (ci === -1 ? 99 : ci);
    });
  const primaryProduct = sortedProducts[0];
  const primaryDate = primaryProduct ? getProductServiceDate(primaryProduct) : null;
  // Fallback: earliest service date across all products
  const fallbackDate = (b.products || [])
    .map(getProductServiceDate)
    .filter(Boolean)
    .sort()[0] || null;

  // Total Client Price = DB grandTotal + supplier cancellation cost from CANCEL_TRAVELER
  // mods (supplier cost is charged to client but not folded into the stored grandTotal).
  // Refund is NEVER subtracted here — it emerges as customer credit / negative saldo
  // when payments exceed the booking value.
  const cancAdj = b.cancellationAdjustment || {};
  const totalPrice = parseFloat(b.grandTotal || b.totalAmount || 0)
    + parseFloat(cancAdj.supplierCost || 0);
  // DUAL POOL — a client-covered transaction cost is part of what the client owes, so
  // outstanding + applied settle against the NET pool (a Sunsky-borne cost is absorbed).
  // Credit also uses the NET pool. Both come live from the payments table; fall back to
  // the persisted paidAmount column if absent.
  const r2 = (v) => Math.round(v * 100) / 100;
  const grossPaid = b.grossPaid != null ? parseFloat(b.grossPaid) : parseFloat(b.totalPaid ?? b.paidAmount ?? 0);
  const netPaid = b.netPaid != null ? parseFloat(b.netPaid) : grossPaid;
  const outstanding = r2(Math.max(0, totalPrice - netPaid));
  const credit = r2(Math.max(0, netPaid - totalPrice));
  const paidAmount = r2(Math.max(0, Math.min(netPaid, totalPrice))); // applied to booking

  return {
    id: b.id,
    ref: b.bookingReference,
    createdAt: b.bookingDate || b.createdAt,
    invoiced: b.invoiced ?? false,
    customer: {
      name: b.customerName || "—",
      email: b.customerEmail || "—",
      type: b.customerType || "Private",
    },
    products,
    productSummaries: summaries,
    startDate: primaryDate || fallbackDate,
    startDateType: primaryProduct?.productType || "—",
    productSummary: b.productSummary || products.join(", "),
    origin: b.bookingType || "Offline",
    totalPrice,
    // paidAmount = amount applied to the booking (net, capped at total). saldo is
    // signed: outstanding (owe us, +) minus credit (we owe client, −). A client-covered
    // txn cost adds to what the client owes; a Sunsky-borne cost is absorbed.
    paidAmount,
    saldo: r2(outstanding - credit),
    lastPaymentDate: b.lastPaymentDate || null,
    paymentStatus: b.paymentStatus || "Unpaid",
    bookingStatus: b.status || "Pending",
    raw: b,
  };
};

// ── Multi-select Product Dropdown ──────────────────────────────────────────
const ProductFilterDropdown = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (key) => {
    if (selected.includes(key)) {
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  const label = selected.length === 0
    ? "All Products"
    : selected.length === 1
    ? ALL_PRODUCT_TYPES.find((p) => p.key === selected[0])?.label
    : `${selected.length} products`;

  return (
    <div className={styles.productDropdownWrap} ref={ref}>
      <button
        className={`${styles.productDropdownBtn} ${selected.length > 0 ? styles.productDropdownBtnActive : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{label}</span>
        {selected.length > 0 && (
          <span className={styles.productDropdownCount}>{selected.length}</span>
        )}
        <ChevronDown size={12} className={`${styles.productDropdownChevron} ${open ? styles.productDropdownChevronOpen : ""}`} />
      </button>

      {open && (
        <div className={styles.productDropdownMenu}>
          <div className={styles.productDropdownHeader}>
            <span>Filter by product</span>
            {selected.length > 0 && (
              <button className={styles.productDropdownClear} onClick={() => onChange([])}>
                Clear all
              </button>
            )}
          </div>
          {ALL_PRODUCT_TYPES.map(({ key, label: pLabel, Icon }) => {
            const isSelected = selected.includes(key);
            const style = PRODUCT_STYLE[key] || {};
            return (
              <button
                key={key}
                className={`${styles.productDropdownItem} ${isSelected ? styles.productDropdownItemSelected : ""}`}
                onClick={() => toggle(key)}
              >
                <span
                  className={styles.productDropdownItemIcon}
                  style={{ background: style.bg, color: style.color }}
                >
                  <Icon size={11} />
                </span>
                <span className={styles.productDropdownItemLabel}>{pLabel}</span>
                {isSelected && <Check size={12} className={styles.productDropdownItemCheck} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Product counts in table cell ──────────────────────────────────────────
const ProductCounts = ({ products }) => {
  const grouped = {};
  products.forEach((p) => { grouped[p] = (grouped[p] || 0) + 1; });
  const entries = Object.entries(grouped);

  return (
    <div className={styles.productCounts}>
      {entries.map(([key, count]) => {
        const Icon = PRODUCT_ICON[key] || FileText;
        const style = PRODUCT_STYLE[key] || {};
        return (
          <span
            key={key}
            className={styles.productCount}
            style={{ background: style.bg, color: style.color, borderColor: style.border }}
            title={key}
          >
            <Icon size={12} />
            <span className={styles.productCountNum}>x{count}</span>
          </span>
        );
      })}
    </div>
  );
};

// ── Product Summary Cell with fixed-position tooltip ─────────────────────
const ProductSummaryCell = ({ summaries }) => {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null); // { top, left }

  if (!summaries || summaries.length === 0) {
    return <span className={styles.summaryEmpty}>—</span>;
  }

  const primary = summaries[0];
  const Icon = PRODUCT_ICON[primary.type] || FileText;
  const pStyle = PRODUCT_STYLE[primary.type] || {};
  const extra = summaries.length - 1;

  const showTip = () => {
    if (extra === 0 || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const tipW = 290;
    const tipH = 48 + summaries.length * 42; // approximate
    let top = rect.bottom + 8;
    let left = rect.left;
    // Keep within viewport
    if (top + tipH > window.innerHeight - 12) top = rect.top - tipH - 8;
    if (left + tipW > window.innerWidth - 12) left = window.innerWidth - tipW - 12;
    setTip({ top, left });
  };

  const hideTip = () => setTip(null);

  return (
    <div
      className={styles.productSummaryWrap}
      ref={wrapRef}
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
    >
      <div className={styles.summaryPrimary}>
        <span className={styles.summaryIcon} style={{ background: pStyle.bg, color: pStyle.color }}>
          <Icon size={10} />
        </span>
        <span className={styles.summaryText}>{primary.text}</span>
        {extra > 0 && <span className={styles.summaryBadge}>+{extra}</span>}
      </div>
      {tip && extra > 0 && (
        <div
          className={styles.summaryTooltip}
          style={{ top: tip.top, left: tip.left }}
        >
          <div className={styles.tooltipArrow} />
          <div className={styles.tooltipHeader}>
            All Products
            <span className={styles.tooltipCount}>{summaries.length}</span>
          </div>
          <div className={styles.tooltipList}>
            {summaries.map((s, i) => {
              const TIcon = PRODUCT_ICON[s.type] || FileText;
              const tStyle = PRODUCT_STYLE[s.type] || {};
              return (
                <div key={i} className={styles.tooltipItem}>
                  <span
                    className={styles.tooltipIcon}
                    style={{ background: tStyle.bg, color: tStyle.color, border: `1px solid ${tStyle.border}` }}
                  >
                    <TIcon size={11} />
                  </span>
                  <div className={styles.tooltipInfo}>
                    <span className={styles.tooltipType}>{s.type === "CarRental" ? "Car Rental" : s.type}</span>
                    <span className={styles.tooltipDetail}>{s.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Three-dot actions menu ────────────────────────────────────────────────
const ActionsMenu = ({ booking, onOpen, onCancel, onDelete, canDelete }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.right - 120 });
    }
    setOpen((o) => !o);
  };

  return (
    <div className={styles.actionsMenuWrap} ref={ref}>
      <button ref={btnRef} className={styles.actionsMenuBtn} onClick={handleToggle}>
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className={styles.actionsMenu} style={{ position: "fixed", top: pos.top, left: pos.left }}>
          <button className={styles.actionsMenuItem} onClick={() => { onOpen(booking); setOpen(false); }}>
            <ExternalLink size={12} /> Open
          </button>
          {booking.bookingStatus !== "Cancelled" && (
            <button className={styles.actionsMenuDanger} onClick={() => { onCancel(booking); setOpen(false); }}>
              <Ban size={12} /> Cancel
            </button>
          )}
          {canDelete && (
            <button className={styles.actionsMenuDanger} onClick={() => { onDelete(booking); setOpen(false); }}>
              <Trash2 size={12} /> Delete Booking
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Generic pill multi-select dropdown (Origin, Payment Status) ────────────
const PillMultiDropdown = ({ label, options, selected, onChange, getKey = (o) => o, getLabel = (o) => o }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (key) => {
    onChange(selected.includes(key) ? selected.filter((k) => k !== key) : [...selected, key]);
  };

  const btnLabel = selected.length === 0
    ? `${label}: All`
    : selected.length === 1
    ? getLabel(options.find((o) => getKey(o) === selected[0]))
    : `${selected.length} selected`;

  return (
    <div className={styles.productDropdownWrap} ref={ref}>
      <button
        className={`${styles.productDropdownBtn} ${selected.length > 0 ? styles.productDropdownBtnActive : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span>{btnLabel}</span>
        {selected.length > 0 && <span className={styles.productDropdownCount}>{selected.length}</span>}
        <ChevronDown size={12} className={`${styles.productDropdownChevron} ${open ? styles.productDropdownChevronOpen : ""}`} />
      </button>
      {open && (
        <div className={styles.productDropdownMenu}>
          <div className={styles.productDropdownHeader}>
            <span>Filter by {label.toLowerCase()}</span>
            {selected.length > 0 && (
              <button className={styles.productDropdownClear} onClick={() => onChange([])}>Clear all</button>
            )}
          </div>
          {options.map((o) => {
            const key = getKey(o);
            const isSelected = selected.includes(key);
            return (
              <button
                key={key}
                className={`${styles.productDropdownItem} ${isSelected ? styles.productDropdownItemSelected : ""}`}
                onClick={() => toggle(key)}
              >
                <span className={styles.productDropdownItemLabel}>{getLabel(o)}</span>
                {isSelected && <Check size={12} className={styles.productDropdownItemCheck} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────
const BookingList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // A caller (e.g. the Dashboard "New Online Bookings" card → Show all) can route
  // here with a pre-applied filter via location.state, so the list opens already
  // scoped (e.g. origins: ["Online"], activeTab: "All").
  const navState = location.state || {};
  const containerRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(navState.activeTab || "Confirmed");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(
    navState.origins ? { ...DEFAULT_FILTERS, origins: navState.origins } : DEFAULT_FILTERS
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const setFilter = (key, value) => { setFilters((p) => ({ ...p, [key]: value })); setPage(1); };

  useEffect(() => {
    const sidebarEl = document.querySelector('[class*="sidebar"]');
    if (!sidebarEl) return;
    const checkCollapsed = () => {
      setSidebarCollapsed(sidebarEl.className.includes("collapsed"));
    };
    checkCollapsed();
    const observer = new MutationObserver(checkCollapsed);
    observer.observe(sidebarEl, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const resolvedStatus = activeTab !== "All" ? activeTab : "";

  const { data: statusCounts } = useGetBookingStatusCounts();

  // ── Pure server-side pagination ──────────────────────────────────────────
  // The API filters AND paginates: every filter (quick + advanced) is sent as a
  // query param and only the current page is returned. No big client fetch.
  // Booking status: the drawer's multi-select wins; otherwise the active tab.
  const statusParam = filters.bookingStatus.length
    ? filters.bookingStatus.join(",")
    : (resolvedStatus || undefined);
  const queryParams = useMemo(() => toQueryParams(filters), [filters]);

  const { data: rawBookings, pagination, loading, error, refetch } = useGetBookings({
    page,
    limit,
    search: searchTerm || undefined,
    status: statusParam,
    ...queryParams,
  });

  // Soft-delete a booking — Super Admins (isAdmin) or anyone granted the
  // booking.delete permission. Mirrors the backend can('booking.delete') gate so
  // the button shows exactly when the API will allow the action.
  const user = useAppSelector(selectUser);
  const canDeleteBookings =
    !!user?.isAdmin || (Array.isArray(user?.permissions) && user.permissions.includes("booking.delete"));
  const { showToast } = useToast();
  const { execute: deleteBookingApi, loading: deletingBooking } = useDeleteBooking();
  const [deleteTarget, setDeleteTarget] = useState(null);
  // When the backend blocks deletion because the booking has payments, we flip
  // the modal into an info-only "clear payments first" state (no Delete button).
  const [deleteBlockMsg, setDeleteBlockMsg] = useState(null);

  const bookings = useMemo(() => (rawBookings || []).map(mapApiBooking), [rawBookings]);

  const handleOpenBooking = (booking) => {
    navigate(`/bookings/${booking.id}`, { state: { booking } });
  };

  const handleCancelBooking = (booking) => {
    navigate("/bookings/cancellation", { state: { booking } });
  };

  const handleDeleteBooking = (booking) => {
    setDeleteBlockMsg(null);
    setDeleteTarget(booking);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteBlockMsg(null);
  };

  const confirmDeleteBooking = async () => {
    if (!deleteTarget) return;
    const res = await deleteBookingApi(deleteTarget.id);
    if (!res.success) {
      // Finance-first block: booking still has payments — switch the modal to an
      // info-only state instead of showing a hard error, and keep it open.
      if (res.code === "BOOKING_HAS_PAYMENTS") {
        setDeleteBlockMsg(res.message);
        throw new Error(res.message);
      }
      showToast(res.message || "Failed to delete booking", "error");
      throw new Error(res.message || "Failed to delete booking"); // keep modal open
    }
    showToast(`Booking ${deleteTarget.ref} deleted`, "success");
    refetch?.();
    // The modal closes itself via onClose after onConfirm resolves.
  };

  const filterCount = activeFilterCount(filters);
  const hasActiveFilters = searchInput !== "" || filterCount > 0;

  // The API already returned exactly the page to show.
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const pageClamped = Math.min(page, totalPages);
  const pageRows = bookings;

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages]);
    for (let i = Math.max(1, pageClamped - 2); i <= Math.min(totalPages, pageClamped + 2); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    let prev = 0;
    for (const p of sorted) {
      if (p > prev + 1) result.push("…");
      result.push(p);
      prev = p;
    }
    return result;
  }, [totalPages, pageClamped]);

  const handleClear = () => {
    setSearchInput("");
    setSearchTerm("");
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <FolderOpen size={22} />
          </div>
          <div className={styles.headerText}>
            <h1>Bookings Management</h1>
            <p>Manage all bookings — online, offline, API, and fetched</p>
          </div>
        </div>
        <button className={styles.addBtn} onClick={() => navigate("/bookings/add")}>
          <Plus size={15} />
          Add Booking
        </button>
      </div>

      <div className={styles.tabBar}>
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const count = statusCounts[tab.key] ?? 0;
          return (
            <button
              key={tab.key}
              className={`${styles.tab} ${styles[`tab${tab.key}`] || ""} ${isActive ? styles.tabActive : ""}`}
              onClick={() => { setActiveTab(tab.key); setPage(1); }}
            >
              {tab.label}
              <span
                className={`${styles.tabBadge} ${
                  !isActive ? styles[`badge${tab.key}`] || "" : ""
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.filterBar}>
        <div className={styles.searchWrap}>
          <Search size={14} className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search booking no, invoice, client, traveler, email, supplier ref, PNR..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className={styles.searchClear} onClick={() => setSearchInput("")}>
              <X size={12} />
            </button>
          )}
        </div>

        <div className={styles.filterDivider} />

        <ProductFilterDropdown selected={filters.productTypes} onChange={(v) => setFilter("productTypes", v)} />

        <PillMultiDropdown
          label="Origin"
          options={ORIGIN_OPTIONS}
          selected={filters.origins}
          onChange={(v) => setFilter("origins", v)}
        />

        <PillMultiDropdown
          label="Payment"
          options={PAYMENT_STATUS_OPTIONS}
          selected={filters.paymentStatuses}
          onChange={(v) => setFilter("paymentStatuses", v)}
          getLabel={(o) => PAYMENT_STATUS_LABEL[o]}
        />

        <div className={styles.dateRangeWrap} title="Service date range">
          <Calendar size={13} className={styles.dateRangeIcon} />
          <input
            type="date"
            className={styles.dateInput}
            value={filters.serviceFrom}
            onChange={(e) => setFilter("serviceFrom", e.target.value)}
          />
          <span className={styles.dateSeparator}>–</span>
          <input
            type="date"
            className={styles.dateInput}
            value={filters.serviceTo}
            onChange={(e) => setFilter("serviceTo", e.target.value)}
          />
        </div>

        <button
          className={`${styles.filtersBtn} ${filterCount > 0 ? styles.filtersBtnActive : ""}`}
          onClick={() => setDrawerOpen(true)}
        >
          <SlidersHorizontal size={14} />
          Filters
          {filterCount > 0 && <span className={styles.filtersBtnBadge}>{filterCount}</span>}
        </button>

        {hasActiveFilters && (
          <button className={styles.clearFilters} onClick={handleClear}>
            <X size={12} />
            Clear filters
          </button>
        )}
      </div>

      <div className={styles.resultsSummary}>
        Showing <strong>{pageRows.length}</strong> of {total} bookings
        {hasActiveFilters && <span className={styles.filtered}>(filtered)</span>}
        {loading && <span className={styles.filtered}>· Loading…</span>}
        {error && <span className={styles.filtered} style={{ color: "#991b1b" }}>· {error}</span>}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <colgroup>
            <col style={{ width: sidebarCollapsed ? "8%" : "9%" }} />
            <col style={{ width: sidebarCollapsed ? "10%" : "11%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: sidebarCollapsed ? "7%" : "8%" }} />
            <col style={{ width: sidebarCollapsed ? "13%" : "16%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: sidebarCollapsed ? "8%" : "10%" }} />
            <col style={{ width: sidebarCollapsed ? "8%" : "9%" }} />
            <col style={{ width: sidebarCollapsed ? "8%" : "9%" }} />
            <col style={{ width: sidebarCollapsed ? "5%" : "6%" }} />
            {sidebarCollapsed && <col style={{ width: "7%" }} />}
            <col style={{ width: 40 }} />
          </colgroup>
          <thead>
            <tr>
              <th>Booking Date</th>
              <th>Order No</th>
              <th>Invoiced</th>
              <th>Products</th>
              <th>Start Date</th>
              <th>Product Summary</th>
              <th>Origin</th>
              <th>Client</th>
              <th>Total Client Price</th>
              <th>Paid Amount</th>
              <th>Saldo</th>
              {sidebarCollapsed && <th>Last Payment Date</th>}
              <th className={styles.thActions}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length > 0 ? (
              pageRows.map((b) => {
                const dt = formatDateTime(b.createdAt);
                const daysBefore = getDaysBefore(b.lastPaymentDate, b.startDate);
                const saldoClass = b.saldo < 0
                  ? styles.saldoCredit
                  : b.saldo === 0
                  ? styles.saldoPaid
                  : b.paidAmount > 0 && b.paidAmount < b.totalPrice
                  ? (b.startDate && new Date(b.startDate) <= new Date() ? styles.saldoOverdue : styles.saldoPartial)
                  : (b.startDate && new Date(b.startDate) <= new Date() ? styles.saldoOverdue : styles.saldoOpen);

                return (
                  <tr key={b.id}>
                    <td>
                      <div className={styles.cellPrimary}>{dt.date}</div>
                      <div className={styles.cellSecondary}>{dt.time}</div>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.bookingRef}
                        onClick={() => handleOpenBooking(b)}
                        title="Open booking details"
                      >
                        {b.ref}
                      </button>
                    </td>
                    <td>
                      <span className={`${styles.invoicedBadge} ${b.invoiced ? styles.invoicedYes : styles.invoicedNo}`}>
                        {b.invoiced ? "YES" : "NO"}
                      </span>
                    </td>
                    <td>
                      <ProductCounts products={b.products} />
                    </td>
                    <td>
                      <div className={styles.cellPrimary}>{formatDate(b.startDate)}</div>
                      <div className={styles.cellSecondary}>({b.startDateType})</div>
                    </td>
                    <td>
                      <ProductSummaryCell summaries={b.productSummaries} />
                    </td>
                    <td>
                      <span className={`${styles.originBadge} ${styles[`origin${b.origin}`] || ""}`}>
                        {b.origin}
                      </span>
                    </td>
                    <td>
                      <div className={styles.customerName}>{b.customer.name}</div>
                    </td>
                    <td>
                      <div className={styles.price}>
                        € {b.totalPrice.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td>
                      <div className={styles.price}>
                        € {b.paidAmount.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                    <td>
                      {/* An overpaid booking shows its negative saldo directly in red
                          (e.g. € -410,00) — the customer is owed that as credit. */}
                      <div
                        className={`${styles.price} ${saldoClass}`}
                        title={b.saldo < -0.005 ? `Customer paid € ${Math.abs(b.saldo).toLocaleString("de-DE", { minimumFractionDigits: 2 })} more than the booking value — owed back as credit.` : undefined}
                      >
                        € {b.saldo.toLocaleString("de-DE", { minimumFractionDigits: 2 })}
                      </div>
                    </td>
                    {sidebarCollapsed && (
                      <td>
                        <div className={`${styles.cellPrimary} ${b.saldo > 0 ? styles.paymentWarning : ""}`}>
                          {formatDate(b.lastPaymentDate)}
                        </div>
                        {daysBefore !== null && (
                          <div className={`${styles.cellSecondary} ${b.saldo > 0 ? styles.paymentWarning : ""}`}>
                            ({daysBefore}d before)
                          </div>
                        )}
                      </td>
                    )}
                    <td>
                      <ActionsMenu
                        booking={b}
                        onOpen={handleOpenBooking}
                        onCancel={handleCancelBooking}
                        onDelete={handleDeleteBooking}
                        canDelete={canDeleteBookings}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={sidebarCollapsed ? 13 : 12}>
                  <div className={styles.emptyState}>
                    <FolderOpen size={36} className={styles.emptyIcon} />
                    <p className={styles.emptyTitle}>No bookings found</p>
                    <p className={styles.emptySubtitle}>Try adjusting your filters or search term</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className={styles.paginationBar}>
          <span className={styles.paginationInfo}>
            Page {pageClamped} of {totalPages} ({total} total)
          </span>
          <div className={styles.pageNumbers}>
            <button
              className={styles.pageNavBtn}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageClamped <= 1}
            >
              <ChevronLeft size={14} />
            </button>
            {pageNumbers.map((p, idx) =>
              p === "…" ? (
                <span key={`e-${idx}`} className={styles.pageEllipsis}>…</span>
              ) : (
                <button
                  key={p}
                  className={`${styles.pageBtn} ${Number(p) === Number(pageClamped) ? styles.pageBtnActive : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              className={styles.pageNavBtn}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageClamped >= totalPages}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={closeDeleteModal}
        title={deleteBlockMsg ? "Can't Delete Booking" : "Delete Booking"}
        message={
          deleteBlockMsg
            ? deleteBlockMsg
            : deleteTarget
              ? `Are you sure you want to delete booking ${deleteTarget.ref}? It will be removed from all lists and reports. This is a Super Admin action and cannot be undone from the interface.`
              : undefined
        }
        confirmLabel="Delete Booking"
        cancelLabel={deleteBlockMsg ? "Close" : "Cancel"}
        hideConfirm={!!deleteBlockMsg}
        loading={deletingBooking}
        onConfirm={confirmDeleteBooking}
      />

      <BookingFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        value={filters}
        onApply={(next) => { setFilters(next); setPage(1); }}
      />
    </div>
  );
};

export default BookingList;
